/**
 * Factory Presets for Audio Effects
 * 
 * Curated presets for each effect type covering common use cases.
 * Each preset includes parameter values and descriptions.
 */

import { EffectPresetsMap, EffectPreset } from '../types/effects';

export const EFFECT_PRESETS: EffectPresetsMap = {
  // Compressor Presets
  compressor: [
    {
      name: 'Gentle Compression',
      description: 'Subtle compression for gluing tracks together',
      parameters: {
        threshold: -18,
        ratio: 2.5,
        attack: 10,
        release: 100,
        makeup: 3,
        mix: 100
      }
    },
    {
      name: 'Punchy Drums',
      description: 'Fast attack for transient control and punch',
      parameters: {
        threshold: -12,
        ratio: 4,
        attack: 1,
        release: 50,
        makeup: 6,
        mix: 100
      }
    },
    {
      name: 'Vocal Leveler',
      description: 'Smooth compression for consistent vocal levels',
      parameters: {
        threshold: -15,
        ratio: 3,
        attack: 20,
        release: 150,
        makeup: 4,
        mix: 100
      }
    },
    {
      name: 'Parallel Compression',
      description: 'Heavy compression blended with dry signal',
      parameters: {
        threshold: -20,
        ratio: 8,
        attack: 5,
        release: 80,
        makeup: 10,
        mix: 40
      }
    }
  ],

  // Flanger Presets
  flanger: [
    {
      name: 'Subtle Sweep',
      description: 'Gentle flanger for subtle movement',
      parameters: {
        rate: 0.3,
        depth: 30,
        feedback: 20,
        delay: 3,
        mix: 30
      }
    },
    {
      name: 'Classic Jet',
      description: 'Traditional jet plane flanger effect',
      parameters: {
        rate: 0.5,
        depth: 70,
        feedback: 60,
        delay: 5,
        mix: 50
      }
    },
    {
      name: 'Metallic Sweep',
      description: 'Aggressive flanger with high feedback',
      parameters: {
        rate: 0.8,
        depth: 80,
        feedback: 80,
        delay: 2,
        mix: 60
      }
    },
    {
      name: 'Slow Motion',
      description: 'Very slow, deep flanger for ambient textures',
      parameters: {
        rate: 0.1,
        depth: 90,
        feedback: 40,
        delay: 7,
        mix: 45
      }
    }
  ],

  // Tremolo Presets
  tremolo: [
    {
      name: 'Gentle Pulse',
      description: 'Subtle tremolo for adding movement',
      parameters: {
        rate: 4,
        depth: 30,
        phase: 0,
        waveform: 0 // sine
      }
    },
    {
      name: 'Classic Tremolo',
      description: 'Traditional amp-style tremolo',
      parameters: {
        rate: 5,
        depth: 60,
        phase: 180,
        waveform: 0 // sine
      }
    },
    {
      name: 'Helicopter',
      description: 'Fast, choppy tremolo effect',
      parameters: {
        rate: 12,
        depth: 90,
        phase: 0,
        waveform: 2 // square
      }
    },
    {
      name: 'Stereo Wobble',
      description: 'Wide stereo tremolo with phase offset',
      parameters: {
        rate: 3,
        depth: 70,
        phase: 90,
        waveform: 1 // triangle
      }
    }
  ],

  // Ping-Pong Delay Presets
  pingpong: [
    {
      name: 'Subtle Echo',
      description: 'Short ping-pong delay for width',
      parameters: {
        delayTime: 250,
        feedback: 30,
        filterFreq: 6000,
        stereoWidth: 70,
        mix: 25
      }
    },
    {
      name: 'Rhythmic Bounce',
      description: 'Medium delay with clear bounces',
      parameters: {
        delayTime: 375,
        feedback: 50,
        filterFreq: 4000,
        stereoWidth: 100,
        mix: 35
      }
    },
    {
      name: 'Dub Delay',
      description: 'Long, filtered delay with high feedback',
      parameters: {
        delayTime: 500,
        feedback: 70,
        filterFreq: 2000,
        stereoWidth: 90,
        mix: 45
      }
    },
    {
      name: 'Ambient Space',
      description: 'Very long delay for atmospheric effects',
      parameters: {
        delayTime: 750,
        feedback: 60,
        filterFreq: 3000,
        stereoWidth: 100,
        mix: 40
      }
    }
  ],

  // Auto-Wah Presets
  autowah: [
    {
      name: 'Subtle Funk',
      description: 'Gentle auto-wah for funky movement',
      parameters: {
        sensitivity: 40,
        minFreq: 400,
        maxFreq: 2000,
        resonance: 50,
        attack: 10,
        release: 100,
        mix: 100
      }
    },
    {
      name: 'Classic Wah',
      description: 'Traditional auto-wah sound',
      parameters: {
        sensitivity: 60,
        minFreq: 300,
        maxFreq: 3000,
        resonance: 70,
        attack: 5,
        release: 80,
        mix: 100
      }
    },
    {
      name: 'Aggressive Quack',
      description: 'Fast, resonant auto-wah',
      parameters: {
        sensitivity: 80,
        minFreq: 500,
        maxFreq: 4000,
        resonance: 85,
        attack: 2,
        release: 50,
        mix: 100
      }
    },
    {
      name: 'Smooth Sweep',
      description: 'Slow, smooth filter sweep',
      parameters: {
        sensitivity: 50,
        minFreq: 200,
        maxFreq: 2500,
        resonance: 60,
        attack: 20,
        release: 150,
        mix: 100
      }
    }
  ],

  // Existing effects (empty arrays for now - can be populated later)
  bitcrusher: [],
  reverb: [],
  delay: [],
  overdrive: [],
  // chorus: [], // DISABLED: Has audio artifacts
  eq: [],
  none: []
};

/**
 * Get presets for a specific effect type
 */
export function getPresetsForEffect(effectType: string): EffectPreset[] {
  return EFFECT_PRESETS[effectType as keyof typeof EFFECT_PRESETS] || [];
}

/**
 * Get a specific preset by name
 */
export function getPresetByName(effectType: string, presetName: string): EffectPreset | undefined {
  const presets = getPresetsForEffect(effectType);
  return presets.find(preset => preset.name === presetName);
}
